import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Button,
  Flex,
  List,
  ListItem,
  ListIcon,
  Icon,
} from "@chakra-ui/react";
import { FaCheckCircle, FaHome, FaVideo, FaCalendarAlt, FaClipboardList, FaSolarPanel, FaDollarSign } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../brandConfig";
import { VapiFloatingCallButton } from "../components/VapiFloatingCallButton";

const AppointmentBookingPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <NavbarWithCallToAction />

      <Box
        bg={getColor("background.main")}
        minHeight="100vh"
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
          radial-gradient(60% 60% at 0% 0%, #112334 0%, transparent 50%),
          radial-gradient(50% 50% at 50% 50%, #369eff33 0%, transparent 100%)
        `,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {/* Hero Section */}
        <Box
          position="relative"
          width="100vw"
          height="500px"
          overflow="hidden"
          marginLeft="50%"
          transform="translateX(-50%)"
          backgroundImage="url('/assets/onesolarimages/services/residential_solar.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
        >
          <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0, 0, 0, 0.5)" zIndex={1} />
          <Container maxW="1200px" position="relative" zIndex={2} height="100%" display="flex" alignItems="center">
            <VStack spacing={6} align="flex-start" maxW="800px">
              <Heading
                as="h1"
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="bold"
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
                textTransform="uppercase"
              >
                Book Your Solar Assessment
              </Heading>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
              >
                Choose between an in-home visit or a convenient Google Meet consultation to start your solar journey
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* Key Benefits Banner */}
        <Box bg="#121c47" py={{ base: 8, md: 12 }} position="relative" zIndex={2}>
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaCalendarAlt size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Flexible Scheduling
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaClipboardList size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Free Assessment
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaSolarPanel size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Expert Advice
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaDollarSign size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  No Obligation Quote
                </Text>
              </Flex>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Main Content */}
        <Box bg="white" py={{ base: 16, md: 20 }} position="relative" zIndex={2}>
          <Container maxW="1200px">
            <VStack spacing={16} align="stretch">
              {/* Introduction */}
              <Box textAlign="center">
                <Box width="60px" height="2px" bg="#121c47" mb={6} mx="auto" />
                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  textTransform="uppercase"
                >
                  Get Ready for Your Solar Journey
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" maxW="900px" mx="auto">
                  Book your free solar assessment today and discover how much you can save with solar energy. Choose the appointment type that works best for you - whether it's an in-home visit for a comprehensive site evaluation or a convenient Google Meet consultation to discuss your solar options.
                </Text>
              </Box>

              {/* Appointment Types */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                {/* In-Home Visit */}
                <Box
                  border="2px solid"
                  borderColor="#121c47"
                  borderRadius="lg"
                  p={8}
                  textAlign="center"
                  transition="all 0.3s"
                  _hover={{ boxShadow: "xl", transform: "translateY(-4px)" }}
                >
                  <Icon as={FaHome} w={16} h={16} color="#121c47" mb={4} />
                  <Heading as="h3" fontSize="2xl" fontWeight="bold" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={4}>
                    In-Home Visit
                  </Heading>
                  <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" mb={6}>
                    Our solar experts will visit your property to conduct a thorough site assessment, analyze your roof structure, measure solar potential, and provide personalized recommendations on-site.
                  </Text>
                  <List spacing={3} textAlign="left" mb={6}>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Comprehensive roof inspection</Text>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Accurate shading analysis</Text>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Electrical system evaluation</Text>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Custom system design</Text>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Instant quote with projections</Text>
                      </Flex>
                    </ListItem>
                  </List>
                  <Text fontSize="sm" color="#666" fontFamily="'Open Sans', sans-serif" fontStyle="italic" mb={4}>
                    Duration: 60-90 minutes
                  </Text>
                </Box>

                {/* Google Meet Consultation */}
                <Box
                  border="2px solid"
                  borderColor="#121c47"
                  borderRadius="lg"
                  p={8}
                  textAlign="center"
                  transition="all 0.3s"
                  _hover={{ boxShadow: "xl", transform: "translateY(-4px)" }}
                >
                  <Icon as={FaVideo} w={16} h={16} color="#121c47" mb={4} />
                  <Heading as="h3" fontSize="2xl" fontWeight="bold" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={4}>
                    Google Meet Consultation
                  </Heading>
                  <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" mb={6}>
                    Join us for a convenient online consultation where we'll review your property via satellite imagery, discuss your energy needs, and provide initial solar recommendations - all from the comfort of your home.
                  </Text>
                  <List spacing={3} textAlign="left" mb={6}>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Satellite roof analysis</Text>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Energy bill review</Text>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Solar potential assessment</Text>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Preliminary system sizing</Text>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#121c47" />
                        <Text fontSize="md" fontFamily="'Open Sans', sans-serif">Budget estimate & savings</Text>
                      </Flex>
                    </ListItem>
                  </List>
                  <Text fontSize="sm" color="#666" fontFamily="'Open Sans', sans-serif" fontStyle="italic" mb={4}>
                    Duration: 30-45 minutes
                  </Text>
                </Box>
              </SimpleGrid>

              {/* What Happens Next */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  textAlign="center"
                >
                  What Happens Next?
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
                  <Box textAlign="center">
                    <Box
                      w="60px"
                      h="60px"
                      borderRadius="full"
                      bg="#121c47"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="2xl"
                      fontWeight="bold"
                      mx="auto"
                      mb={4}
                    >
                      1
                    </Box>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={2}>
                      Book Your Appointment
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Choose your preferred date and time for either an in-home visit or Google Meet consultation
                    </Text>
                  </Box>

                  <Box textAlign="center">
                    <Box
                      w="60px"
                      h="60px"
                      borderRadius="full"
                      bg="#121c47"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="2xl"
                      fontWeight="bold"
                      mx="auto"
                      mb={4}
                    >
                      2
                    </Box>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={2}>
                      Free Assessment
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Our solar experts will assess your property and energy needs, providing tailored recommendations
                    </Text>
                  </Box>

                  <Box textAlign="center">
                    <Box
                      w="60px"
                      h="60px"
                      borderRadius="full"
                      bg="#121c47"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="2xl"
                      fontWeight="bold"
                      mx="auto"
                      mb={4}
                    >
                      3
                    </Box>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={2}>
                      Receive Your Quote
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Get a detailed, no-obligation quote with projected savings and system specifications
                    </Text>
                  </Box>

                  <Box textAlign="center">
                    <Box
                      w="60px"
                      h="60px"
                      borderRadius="full"
                      bg="#121c47"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="2xl"
                      fontWeight="bold"
                      mx="auto"
                      mb={4}
                    >
                      4
                    </Box>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={2}>
                      Make Your Decision
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Take your time to review our proposal and decide if solar is right for you - no pressure!
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* What to Prepare */}
              <Box bg="#f8f9fa" p={{ base: 6, md: 8 }} borderRadius="lg">
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  textAlign="center"
                >
                  How to Prepare for Your Appointment
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      For In-Home Visits
                    </Heading>
                    <List spacing={2}>
                      <ListItem>
                        <Flex align="flex-start">
                          <ListIcon as={FaCheckCircle} color="#121c47" mt={1} />
                          <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">Have your recent electricity bills ready</Text>
                        </Flex>
                      </ListItem>
                      <ListItem>
                        <Flex align="flex-start">
                          <ListIcon as={FaCheckCircle} color="#121c47" mt={1} />
                          <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">Provide clear access to your roof space</Text>
                        </Flex>
                      </ListItem>
                      <ListItem>
                        <Flex align="flex-start">
                          <ListIcon as={FaCheckCircle} color="#121c47" mt={1} />
                          <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">Know your electrical switchboard location</Text>
                        </Flex>
                      </ListItem>
                      <ListItem>
                        <Flex align="flex-start">
                          <ListIcon as={FaCheckCircle} color="#121c47" mt={1} />
                          <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">List any future plans (EV charging, pool, etc.)</Text>
                        </Flex>
                      </ListItem>
                    </List>
                  </Box>

                  <Box>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      For Google Meet Consultations
                    </Heading>
                    <List spacing={2}>
                      <ListItem>
                        <Flex align="flex-start">
                          <ListIcon as={FaCheckCircle} color="#121c47" mt={1} />
                          <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">Have digital copies of your power bills</Text>
                        </Flex>
                      </ListItem>
                      <ListItem>
                        <Flex align="flex-start">
                          <ListIcon as={FaCheckCircle} color="#121c47" mt={1} />
                          <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">Take photos of your roof (if possible)</Text>
                        </Flex>
                      </ListItem>
                      <ListItem>
                        <Flex align="flex-start">
                          <ListIcon as={FaCheckCircle} color="#121c47" mt={1} />
                          <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">Note your home's age and roof type</Text>
                        </Flex>
                      </ListItem>
                      <ListItem>
                        <Flex align="flex-start">
                          <ListIcon as={FaCheckCircle} color="#121c47" mt={1} />
                          <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">Test your internet connection beforehand</Text>
                        </Flex>
                      </ListItem>
                    </List>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Booking Form Section */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  textAlign="center"
                >
                  Book Your Free Solar Assessment
                </Heading>
                <Box
                  bg="white"
                  borderRadius="lg"
                  boxShadow="lg"
                  overflow="hidden"
                  minHeight="900px"
                >
                  <iframe
                    src="https://api.leadconnectorhq.com/widget/form/kxni0J3DQQqxmIfmpHUR"
                    style={{
                      width: '100%',
                      height: '900px',
                      border: 'none',
                      borderRadius: '3px'
                    }}
                    id="inline-appointment-booking"
                    data-layout="{'id':'INLINE'}"
                    data-trigger-type="alwaysShow"
                    data-trigger-value=""
                    data-activation-type="alwaysActivated"
                    data-activation-value=""
                    data-deactivation-type="neverDeactivate"
                    data-deactivation-value=""
                    data-form-name="Appointment Booking Form"
                    data-height="900"
                    data-layout-iframe-id="inline-appointment-booking"
                    data-form-id="kxni0J3DQQqxmIfmpHUR"
                    title="Appointment Booking Form"
                  />
                </Box>
              </Box>

              {/* Why Choose One Solar */}
              <Box bg="#f0f7ff" p={{ base: 6, md: 8 }} borderRadius="lg" border="2px solid" borderColor="#121c47">
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#121c47"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  textAlign="center"
                >
                  Why Choose One Solar New Zealand?
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <Box textAlign="center">
                    <Text fontSize="4xl" mb={3}>⭐</Text>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={2}>
                      5-Star Service
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Rated excellent by hundreds of satisfied Kiwi customers across New Zealand
                    </Text>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="4xl" mb={3}>🏆</Text>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={2}>
                      Premium Quality
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Top-tier solar panels and inverters with comprehensive warranties
                    </Text>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="4xl" mb={3}>🛡️</Text>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={2}>
                      Peace of Mind
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Fully licensed, insured, and backed by industry-leading support
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Alternative Contact CTA */}
              <Box
                bg="#121c47"
                color="white"
                p={{ base: 8, md: 12 }}
                borderRadius="lg"
                textAlign="center"
              >
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                  fontWeight="bold"
                  fontFamily="'Open Sans', sans-serif"
                  mb={4}
                >
                  Prefer to Talk First?
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  maxW="700px"
                  mx="auto"
                >
                  No problem! Give us a call on 0800 767 024 or fill out our contact form and we'll get back to you within 24 hours.
                </Text>
                <Flex gap={4} justify="center" flexWrap="wrap">
                  <Button
                    size="lg"
                    bg="white"
                    color="#121c47"
                    px={8}
                    py={6}
                    fontSize="md"
                    fontWeight="600"
                    textTransform="uppercase"
                    borderRadius="4px"
                    fontFamily="'Open Sans', sans-serif"
                    _hover={{ bg: "gray.100", transform: "translateY(-2px)", boxShadow: "lg" }}
                    transition="all 0.3s"
                    as="a"
                    href="tel:0800767024"
                  >
                    Call 0800 767 024
                  </Button>
                  <Button
                    size="lg"
                    bg="transparent"
                    color="white"
                    px={8}
                    py={6}
                    fontSize="md"
                    fontWeight="600"
                    textTransform="uppercase"
                    borderRadius="4px"
                    fontFamily="'Open Sans', sans-serif"
                    border="2px solid white"
                    _hover={{ bg: "rgba(255,255,255,0.1)", transform: "translateY(-2px)", boxShadow: "lg" }}
                    transition="all 0.3s"
                    onClick={() => navigate("/contact")}
                  >
                    Contact Form
                  </Button>
                </Flex>
              </Box>
            </VStack>
          </Container>
        </Box>

        <FooterWithFourColumns />
      </Box>
      <VapiFloatingCallButton />
    </>
  );
};

export default AppointmentBookingPage;
